<template>
    <!-- Marketing Mode -->
    <li
        v-if="!settings.is_sourcing_mode"
        :class="isLarge ? 'gap-3 at1024:gap-6 p-4 at1024:p-8' : 'gap-3 p-4'"
        class="bg-white border border-gray-325 flex flex-col rounded-md at1024:border-gray-250"
    >
        <!-- Tracking -->
        <h3 :class="isLarge ? 'text-sm': 'text-xs'">
            Tracking # <Link
                v-if="fromCity && toCity && status && order?.id"
                :href="route('web.account.sales.orders.shipment_show', {
                    order: order.id,
                    tracking_number: trackingNumber,
                })"
                class="font-semibold underline hover:text-red"
            >
                {{ trackingNumber }}
            </Link>
            <span v-else>
                <a
                    :href="trackingUrl"
                    target="_blank"
                    class="font-semibold underline hover:text-red"
                >
                    {{ trackingNumber }}
                </a>
            </span>
        </h3>
        <!-- Status -->
        <p :class="['bg-gray-125 flex items-center p-3 rounded-[10px] at1200:p-4 at1350:grow', status.borderStyle]">
          <span aria-hidden="true" class="whitespace-nowrap h-8 w-fit flex items-center gap-2 leading-none text-sm">
            <!-- Status Icon -->
            <BaseSvg :name="status.icon" class="h-4 shrink-0 w-auto"/>
            <b v-if="status.text">{{ status.text }}</b>
            <b v-else>
                Details Unavailable
            </b>
          </span>
            <span class="border-l border-gray-250 leading-tight ml-4 pl-4 text-xs">
        {{ statusDescription }}
      </span>
        </p>
        <div :class="isLarge ? 'hidden at1024:flex' : 'hidden'" class="justify-between gap-6">
            <InfoDisplay
                class="w-1/3"
                v-for="time in times"
                label-classes="text-xs type-secondary"
                value-classes="text-sm type-primary"
                :label="time.label"
            >
                <ShippingDateFormat :date="time.date" />
            </InfoDisplay>
        </div>
        <!-- From / Arrow / To -->
        <p v-if="fromLocation && toLocation" class="flex gap-5 leading-none text-sm">
            <!-- From -->
            <span class="flex flex-col max-w-[38%] shrink-0">
              <span class="text-gray text-xs">From</span>
              <span class="truncate">{{ fromLocation }}</span>
            </span>
            <!-- Arrow -->
            <span aria-hidden="true" class="grow h-10 overflow-hidden relative">
              <BaseSvg name="ShippingArrowLong" class="absolute right-0 top-1/2 -translate-y-1/2" />
                  <!-- Seclock Icon -->
              <BaseSvg
                  v-if="showSeclockIcon && status.text.toLowerCase() !== 'details unavailable' && (estimatedDelivery || deliveryDate)"
                  name="SeclockIcon"
                  :class="seclockIconClasses"
                  :style="seclockIconStyles"
              />
            </span>
            <!-- To -->
            <span class="flex flex-col max-w-[38%] shrink-0">
              <span class="capitalize text-gray text-xs">To</span>
              <span class="truncate">{{ toLocation }}</span>
            </span>
        </p>
    </li>

    <!-- Sourcing Mode -->
    <li
        v-if="settings.is_sourcing_mode"
        class="gap-3 p-6 border border-keyline-primary flex flex-col rounded-md bg-gray-tier-4-light"
    >
        <!-- Tracking -->
        <h3 class="text-sm">
            Tracking # <span>
                <a
                    :href="trackingUrl"
                    target="_blank"
                    class="font-semibold underline decoration-dotted underline-offset-6 hover:decoration-solid ml-1"
                >
                    {{ trackingNumber }}
                </a>
            </span>
        </h3>
        <!-- Status -->
        <p :class="['bg-gray-tier-6 flex items-center p-3 rounded-[10px] at1200:p-4 at1350:grow', status.borderStyle]">
          <span aria-hidden="true" class="whitespace-nowrap h-8 w-fit flex items-center gap-2 leading-none text-sm">
            <!-- Status Icon -->
            <BaseSvg :name="status.icon" class="h-4 shrink-0 w-auto type-secondary"/>
            <b v-if="status.text">{{ status.text }}</b>
            <b v-else>
                Details Unavailable
            </b>
          </span>
            <span class="border-l border-gray-250 leading-tight ml-4 pl-4 text-xs">
        {{ statusDescription }}
      </span>
        </p>
        <div :class="isLarge ? 'hidden at1024:flex' : 'hidden'" class="justify-between gap-6">
            <InfoDisplay
                class="w-1/3"
                v-for="time in times"
                label-classes="text-xs type-secondary"
                value-classes="text-sm type-primary"
                :label="time.label"
            >
                <ShippingDateFormat :date="time.date" />
            </InfoDisplay>
        </div>
        <!-- From / Arrow / To -->
        <p v-if="fromLocation && toLocation" class="flex gap-5 leading-none text-sm">
            <!-- From -->
            <span class="flex flex-col max-w-[38%] shrink-0">
              <span class="text-gray text-xs">From</span>
              <span class="truncate">{{ fromLocation }}</span>
            </span>
            <!-- Arrow -->
            <span aria-hidden="true" class="grow h-10 overflow-hidden relative">
              <BaseSvg name="ShippingArrowLong" class="absolute right-0 top-1/2 -translate-y-1/2" />
                  <!-- Seclock Icon -->
              <BaseSvg
                  v-if="showSeclockIcon && status.text.toLowerCase() !== 'details unavailable' && (estimatedDelivery || deliveryDate)"
                  name="SeclockIcon"
                  :class="seclockIconClasses"
                  :style="seclockIconStyles"
              />
            </span>
            <!-- To -->
            <span class="flex flex-col max-w-[38%] shrink-0">
              <span class="capitalize text-gray text-xs">To</span>
              <span class="truncate">{{ toLocation }}</span>
            </span>
        </p>
    </li>
</template>

<script setup>
import {computed, defineProps} from 'vue';
import {BaseSvg} from "@/Components";
import {useHelpers, useShipping, useUser} from "@/Composables";
import {ShippingDateFormat, InfoDisplay} from "@/Themes/Marketing";

const { getStatusResponse } = useShipping();
const { toTitleCase } = useHelpers();
const { settings } = useUser();

const props = defineProps({
    order: {
        type: Object,
        required: true,
    },
    trackingNumber: {
        type: String,
        required: true,
    },
    trackingUrl: {
        type: String,
    },
    status: {
        type: String,
        required: true,
    },
    statusDescription: {
        type: String,
        required: true,
    },
    fromCity: {
        type: String,
        required: true,
    },
    fromState: {
        type: String,
        required: true,
    },
    toCity: {
        type: String,
        required: true,
    },
    toState: {
        type: String,
        required: true,
    },
    seclockPosition: {
        // Number from 0 to 100 or string 'left' or 'right'
        type: [Number, String],
        default: 'right',
    },
    isLarge: {
        type: Boolean,
        default: false,
    },
    estimatedDelivery: {
        type: [Number, String],
        required: false,
    },
    deliveryDate: {
        type: [Number, String],
        required: false,
    },
    dateShipped: {
        type: [Number, String],
        required: false,
    },
    updatedAt: {
        type: [Number, String],
        required: false,
    },
});

const status = getStatusResponse(props.status);

// Computed Properties
const fromLocation = computed(() => {
  if(props.fromCity && props.fromState) {
    return `${toTitleCase(props.fromCity)}, ${props.fromState}`;
  } else {
      return props.fromCity ?? null;
  }
});

const toLocation = computed(() => {
  if(props.toCity && props.toState) {
    return `${toTitleCase(props.toCity)}, ${props.toState}`;
  } else {
      return props.toCity ?? null;
  }
});

const seclockIconClasses = computed(() => {
    let classes = 'absolute bg-white border-4 border-gray-tier-2 hidden rounded-full size-6 top-1/2 -translate-y-1/2 at768:block';
    if (props.seclockPosition === 'left') {
        classes += ' left-0';
    } else if (props.seclockPosition === 'right') {
        classes += ' right-0';
    } else {
        classes += ' -translate-x-1/2';
    }
    return classes;
});

const seclockIconStyles = computed(() => {
    if (typeof props.seclockPosition === 'number') {
        // Assume position is a percentage
        return { left: `${props.seclockPosition}%` };
    }
    return {};
});

const showSeclockIcon = computed(() => {
    // Show the icon only if position is specified
    return props.seclockPosition !== null && props.seclockPosition !== undefined;
});

const times = computed(() => [
    {
        label: "Date shipped",
        date: props.dateShipped,
    },
    {
        label: "Last updated",
        date: props.updatedAt ?? new Date().toISOString(),
    },
    {
        label: props.deliveryDate ? 'Delivered' : "Estimated delivery",
        date: props.deliveryDate ? props.deliveryDate :props.estimatedDelivery,
    },
]);
</script>
